import {
	doOverlap,
	LassoContainerContext
} from "client/Components";
import { AvailableAppModel } from "client/Api/Workspace/workspace";
import {
	ItemSelectionKind,
	SelectableItemOptions,
	useResizeObserver
} from "client/Hooks";
import { Rect } from "client/Misc/commonTypes";
import { generateTrackingId } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import { IconCard } from "client/Features/Apps/ChooseAppIconDialog/IconCard";

export type AppListItemProps = {
	model: AvailableAppModel;
	selectable: SelectableItemOptions;
};

export const AppListItem = (props: AppListItemProps): ReactElement => {
	const {
		model,
		selectable
	} = props;

	const [doesOverlapWithLasso, setDoesOverlapWithLasso] = useState<boolean>(false);
	const [itemRect, setItemRect] = useState<Rect>({
		left: 0,
		top: 0,
		right: 0,
		bottom: 0
	});

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const rowSize: SizeInformation = useResizeObserver(itemRef);

	let isSelected: boolean;
	let onSelect: ((selectionKind: ItemSelectionKind) => void) | undefined;

	if (selectable === false) {
		isSelected = false;
		onSelect = undefined;
	} else {
		isSelected = selectable.isSelected;
		onSelect = selectable.onSelect;
	}

	const {
		name,
		installedAppId,
		image,
		color
	} = model;

	const {
		hasLassoContainer,
		lassoRect,
		containerRect
	} = useContext(LassoContainerContext);

	useLayoutEffect(
		() => {
			if (!hasLassoContainer) {
				return;
			}

			if (itemRef.current) {
				const {
					left,
					top,
					right,
					bottom
				} = itemRef
					.current
					.getBoundingClientRect();

				const xOffset: number = containerRect.left;
				const yOffset: number = containerRect.top;

				setItemRect({
					left: left - xOffset,
					top: top - yOffset,
					right: right - xOffset,
					bottom: bottom - yOffset
				});
			}
		},
		[
			containerRect.left,
			containerRect.top,
			hasLassoContainer,
			rowSize.width,
			rowSize.height
		]
	);

	useEffect(
		() => {
			if (!hasLassoContainer || !lassoRect || !itemRect) {
				setDoesOverlapWithLasso(false);
			} else {
				setDoesOverlapWithLasso(doOverlap(lassoRect, itemRect));
			}
		},
		[
			hasLassoContainer,
			lassoRect,
			itemRect
		]
	);

	const hasLassoRect: boolean = hasLassoContainer && Boolean(lassoRect);

	useEffect(
		() => {
			if (onSelect && hasLassoRect) {
				onSelect(
					doesOverlapWithLasso
						? ItemSelectionKind.AddToScope
						: ItemSelectionKind.RemoveFromScope
				);
			}
		},
		[
			doesOverlapWithLasso,
			hasLassoRect,
			onSelect
		]
	);

	return (
		<div ref={itemRef}>
			<IconCard
				name={name}
				icon={image}
				iconBackgroundColor={color}
				isSelected={isSelected}
				onSelect={() => onSelect?.(ItemSelectionKind.Append)}
				trackingId={generateTrackingId("app-item", `|${installedAppId}`)}
			/>
		</div>
	);
};
AppListItem.displayName = "AppListItem";
